import type { MedplumClient } from '@medplum/core';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import type { CompletedForm } from '../pages/clients/data/types';

function questionnaireResponseIdFromTaskOutput(
  output: { valueReference?: { reference?: string } }[] | undefined,
): string | undefined {
  for (const row of output ?? []) {
    const ref = row.valueReference?.reference;
    if (ref?.startsWith('QuestionnaireResponse/')) {
      return ref.replace('QuestionnaireResponse/', '');
    }
  }
  return undefined;
}

/** Load the QuestionnaireResponse for a completed form row (chart Forms & Files). */
export async function loadCompletedFormQuestionnaireResponse(
  medplum: MedplumClient,
  form: CompletedForm,
): Promise<QuestionnaireResponse | null> {
  const candidates: string[] = [];
  if (form.questionnaireResponseId) {
    candidates.push(form.questionnaireResponseId);
  }

  if (form.sourceTaskId) {
    try {
      const task = await medplum.readResource('Task', form.sourceTaskId);
      const fromOutput = questionnaireResponseIdFromTaskOutput(task.output);
      if (fromOutput) {
        candidates.push(fromOutput);
      }
    } catch {
      // fall through to search
    }

    try {
      const linked = await medplum.searchResources('QuestionnaireResponse', {
        'based-on': `Task/${form.sourceTaskId}`,
        _sort: '-_lastUpdated',
        _count: '1',
      });
      const qrId = linked[0]?.id;
      if (qrId) {
        candidates.push(qrId);
      }
    } catch {
      // ignore search failure
    }
  }

  const seen = new Set<string>();
  for (const qrId of candidates) {
    if (!qrId || seen.has(qrId)) continue;
    seen.add(qrId);
    try {
      return await medplum.readResource('QuestionnaireResponse', qrId);
    } catch {
      // try next candidate
    }
  }

  return null;
}
